import React, { useMemo, useState } from 'react';
import { Grid, Collapse } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import { ProjectCard } from './ProjectCard';
import { Project } from '../types';
import { useStyles } from '../styles';
import { STYLE } from 'constants/style';

interface ProjectColumnProps {
  projects: Project[];
  projectStatus: number;
  onMenuClick: (event: React.MouseEvent<HTMLElement>, project: Project) => void;
  onViewMissingRequirements?: (project: Project) => void;
}

export const ProjectColumn: React.FC<ProjectColumnProps> = ({ projects, projectStatus, onMenuClick, onViewMissingRequirements }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(true);

  const {
    label: statusLabel,
    key: statusKey,
    color: statusColor,
    backgroundColor: statusBackgroundColor,
  } = useMemo(() => {
    if (projectStatus === 3) {
      return {
        key: 'past',
        label: t('projectList.status.past'),
        color: STYLE.COLOR.ICONS3,
        backgroundColor: STYLE.COLOR.BOX_SHADOW,
      };
    } else if (projectStatus === 1) {
      return {
        key: 'current',
        label: t('projectList.status.current'),
        color: STYLE.COLOR.SUCCESS,
        backgroundColor: STYLE.COLOR.SUCCESS_BACKGROUND,
      };
    }
    return {
      key: 'pending',
      label: t('projectList.status.pending'),
      color: STYLE.COLOR.WARNING,
      backgroundColor: STYLE.COLOR.PENDING,
    };
  }, [projectStatus, t]);

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <Grid item xs={12} sm={6} md={4}>
      <div
        className={classes.columnContent}
        style={{ backgroundColor: statusBackgroundColor }}
        data-testid={`project-column-${statusKey}`}
      >
        <div
          className={`${classes.columnHeader} ${!isExpanded ? classes.columnHeaderCollapsed : ''}`}
          data-testid={`project-column-toggle-${statusKey}`}
          onClick={handleToggle}
        >
          <div className={classes.columnTag} style={{ backgroundColor: statusColor }}>
            <p className={classes.columnTagText}>{statusLabel}</p>
          </div>
          <ExpandMoreIcon
            className={`${classes.columnHeaderIcon} ${isExpanded ? classes.columnHeaderIconExpanded : ''}`}
          />
        </div>
        <Collapse in={isExpanded} timeout="auto">
          <div className={classes.columnProjectsWrapper}>
            {projects?.length > 0 ? (
              projects.map(project => (
                <div key={project?.project?.id} className={classes.columnProjectCard}>
                  <ProjectCard project={project} onMenuClick={onMenuClick} onViewMissingRequirements={onViewMissingRequirements} />
                </div>
              ))
            ) : (
              <div className={classes.columnProjectCard}>
                <p className={classes.noProjectsText} data-testid={`project-column-empty-${statusKey}`}>
                  {t('projectList.emptyState.noProjects')}
                </p>
              </div>
            )}
          </div>
        </Collapse>
      </div>
    </Grid>
  );
};
